import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ChevronRight,
  ChevronDown,
  FolderPlus,
  Pencil,
  Trash2,
  Folder,
  Images,
} from "lucide-react";
import { api, type ApiResponse } from "@/api/client";

export interface AlbumFolder {
  id: string;
  name: string;
  parentId: string | null;
  count: number;
}

interface TreeNode extends AlbumFolder {
  children: TreeNode[];
}

function buildTree(folders: AlbumFolder[]): TreeNode[] {
  const byId = new Map<string, TreeNode>(folders.map((f) => [f.id, { ...f, children: [] }]));
  const roots: TreeNode[] = [];
  for (const node of byId.values()) {
    if (node.parentId && byId.has(node.parentId)) byId.get(node.parentId)!.children.push(node);
    else roots.push(node);
  }
  return roots;
}

/**
 * Reusable album (media_folders) tree. Used as a filter in both MediaPage and the
 * MediaPicker library tab; pass `manage` to expose create/rename/delete controls.
 */
export default function AlbumTree({
  selectedId,
  onSelect,
  manage = false,
}: {
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  manage?: boolean;
}) {
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: ["media-folders"],
    queryFn: () => api.get<ApiResponse<AlbumFolder[]>>("/api/admin/media-folders"),
  });
  const folders = useMemo(() => data?.data ?? [], [data]);
  const tree = useMemo(() => buildTree(folders), [folders]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["media-folders"] });

  const createMutation = useMutation({
    mutationFn: (vars: { name: string; parentId: string | null }) =>
      api.post("/api/admin/media-folders", vars),
    onSuccess: invalidate,
  });
  const renameMutation = useMutation({
    mutationFn: (vars: { id: string; name: string }) =>
      api.patch(`/api/admin/media-folders/${vars.id}`, { name: vars.name }),
    onSuccess: invalidate,
  });
  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/media-folders/${id}`),
    onSuccess: (_res, id) => {
      if (selectedId === id) onSelect(null);
      invalidate();
    },
  });

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const renderNode = (node: TreeNode, depth: number) => {
    const isOpen = expanded.has(node.id);
    const hasChildren = node.children.length > 0;
    return (
      <li key={node.id}>
        <div
          className={`group flex items-center gap-1 rounded px-1.5 py-1 text-sm ${
            selectedId === node.id
              ? "bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300"
              : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
          }`}
          style={{ paddingLeft: `${depth * 12 + 4}px` }}
        >
          <button
            type="button"
            onClick={() => hasChildren && toggle(node.id)}
            className={`shrink-0 ${hasChildren ? "" : "invisible"}`}
            aria-label={isOpen ? "Collapse" : "Expand"}
          >
            {isOpen ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
          </button>
          <button
            type="button"
            onClick={() => onSelect(node.id)}
            className="flex flex-1 items-center gap-1.5 truncate text-left"
          >
            <Folder className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{node.name}</span>
            <span className="text-xs text-gray-400">{node.count}</span>
          </button>
          {manage && (
            <span className="flex shrink-0 gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
              <button
                type="button"
                title="New sub-album"
                onClick={() => {
                  const name = prompt("New sub-album name");
                  if (name?.trim()) createMutation.mutate({ name: name.trim(), parentId: node.id });
                }}
              >
                <FolderPlus className="h-3.5 w-3.5 text-gray-400 hover:text-gray-700" />
              </button>
              <button
                type="button"
                title="Rename"
                onClick={() => {
                  const name = prompt("Rename album", node.name);
                  if (name?.trim()) renameMutation.mutate({ id: node.id, name: name.trim() });
                }}
              >
                <Pencil className="h-3.5 w-3.5 text-gray-400 hover:text-gray-700" />
              </button>
              <button
                type="button"
                title="Delete album"
                onClick={() => {
                  if (confirm(`Delete album "${node.name}"? Images are not deleted.`))
                    deleteMutation.mutate(node.id);
                }}
              >
                <Trash2 className="text-error-400 hover:text-error-600 h-3.5 w-3.5" />
              </button>
            </span>
          )}
        </div>
        {isOpen && hasChildren && (
          <ul>{node.children.map((child) => renderNode(child, depth + 1))}</ul>
        )}
      </li>
    );
  };

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => onSelect(null)}
          className={`flex flex-1 items-center gap-1.5 rounded px-1.5 py-1 text-sm ${
            selectedId === null
              ? "bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300"
              : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
          }`}
        >
          <Images className="h-3.5 w-3.5" /> All images
        </button>
        {manage && (
          <button
            type="button"
            title="New album"
            onClick={() => {
              const name = prompt("New album name");
              if (name?.trim()) createMutation.mutate({ name: name.trim(), parentId: null });
            }}
            className="text-gray-400 hover:text-gray-700"
          >
            <FolderPlus className="h-4 w-4" />
          </button>
        )}
      </div>
      <ul>{tree.map((node) => renderNode(node, 0))}</ul>
    </div>
  );
}
